import React from 'react';
import { Alert, Box, Container } from '@mui/material';
import { Link, Outlet, useLocation } from 'react-router-dom';

import ChatPanelDock from '../../features/chatbot/surfaces/ChatPanelDock';
import { useAppSelector } from '../hooks/redux';

import Header from './Header';

interface LayoutProps {
  showHeader?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

const Layout: React.FC<LayoutProps> = ({ showHeader = true, maxWidth = false }) => {
  const location = useLocation();
  const { isAuthenticated, initializing } = useAppSelector((state) => state.auth);
  const authResolved = !initializing;
  const scopedAiEnabled = isAuthenticated && isScopedAiRoute(location.pathname);
  const showAnonBanner =
    authResolved && !isAuthenticated && location.pathname.startsWith('/curriculum/');

  return (
    <Box
      className="app-layout"
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {showHeader && (
        <Box
          className="app-header"
          sx={{
            flexShrink: 0,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Header />
        </Box>
      )}

      <Box
        className="app-layout-main"
        sx={{ flex: 1, display: 'flex', minWidth: 0, overflow: 'hidden' }}
      >
        <Box
          className="app-layout-content"
          data-app-scroll-container="true"
          sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}
        >
          <Container
            className="app-layout-container"
            disableGutters
            maxWidth={maxWidth}
            sx={{ height: '100%' }}
          >
            {showAnonBanner && (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                You're viewing a public curriculum. <Link to="/login">Sign in</Link> to edit, share,
                or create your own.
              </Alert>
            )}
            <Outlet />
          </Container>
        </Box>
        <Box className="app-scoped-ai">
          <ChatPanelDock enabled={scopedAiEnabled} />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;

function isScopedAiRoute(pathname: string) {
  return (
    /^\/curriculum\/courses\/[^/]+(?:\/units\/[^/]+(?:\/lessons\/[^/]+)?)?$/.test(pathname) ||
    /^\/curriculum\/units\/[^/]+$/.test(pathname) ||
    /^\/curriculum\/lessons\/[^/]+$/.test(pathname) ||
    /^\/curriculum\/resources\/[^/]+\/edit$/.test(pathname) ||
    /^\/curriculum\/resources\/[^/]+\/slides$/.test(pathname)
  );
}
